import { useEffect, useMemo, useState } from "react";
import api, { formatError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { Calculator, Plus, Trash, Download, Save, AlertCircle } from "lucide-react";

const currentMonthKey = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const computeSessionCount = (start, end, minutesPerSession) => {
  if (!start || !end || !minutesPerSession) return 0;
  try {
    const [sh, sm] = start.split(":").map(Number);
    const [eh, em] = end.split(":").map(Number);
    const mins = eh * 60 + em - (sh * 60 + sm);
    if (mins <= 0) return 0;
    // ((End - Start as fraction of day) * 1440 minutes-per-day) / duration-per-session
    // This is mathematically equivalent to `mins / duration` since `(mins/1440) * 1440 = mins`.
    return Math.floor(mins / Number(minutesPerSession));
  } catch { return 0; }
};

// "13:45" → "1:45 PM"
const to12Hour = (t) => {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  if (Number.isNaN(h)) return "";
  const suffix = h >= 12 ? "PM" : "AM";
  const hh = ((h + 11) % 12) + 1;
  return `${hh}:${String(m || 0).padStart(2, "0")} ${suffix}`;
};

const EMPTY_DATE_ROW = { date: "", start_time: "", end_time: "", sessions_count: "", remark: "" };

const buildEmpRow = (emp) => ({
  employee_id: emp.id,
  employee_name: emp.name,
  job_type: emp.job_type || "",
  mode: emp.rate_type === "per_session" ? "per_session" : "per_day",
  rate_amount: emp.rate_amount || 0,
  rate_per_session: emp.rate_per_session || 0,
  working_days: 0,
  non_working_days: 0,
  total_sessions: 0,
  total_override: "",
  remark: "",
});

export default function ServiceOrderCalculation({ order, isMasterAdmin, onClose }) {
  const [calcs, setCalcs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadCalcs = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/service-orders/${order.id}/calculations`);
      setCalcs(data || []);
    } catch (e) { toast.error(formatError(e)); }
    finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadCalcs(); }, [order.id]);

  const openNew = () => {
    setSelectedId(null);
    setForm({
      month_key: currentMonthKey(),
      minutes_per_session: 60,
      school_rate_per_session: 0,
      session_dates: [],
      invoice_total_override: "",
      // Show active employees + those marked inactive only when sessions were performed.
      employee_calculations: (order.contractual_employees || [])
        .filter((e) => e.is_active !== false || e.did_sessions === true)
        .map(buildEmpRow),
      notes: "",
    });
  };

  const openExisting = (c) => {
    setSelectedId(c.id);
    setForm({
      month_key: c.month_key,
      minutes_per_session: c.minutes_per_session || 60,
      school_rate_per_session: c.school_rate_per_session || 0,
      session_dates: (c.session_dates || []).map((sd) => ({ ...sd, sessions_count: String(sd.sessions_count ?? "") })),
      invoice_total_override: c.invoice_total_override ?? "",
      employee_calculations: (c.employee_calculations || []).map((ec) => ({
        ...ec,
        total_override: ec.total_override ?? "",
      })),
      notes: c.notes || "",
    });
  };

  // ---- Session date helpers ----
  const addDateRow = () => setForm({ ...form, session_dates: [...form.session_dates, { ...EMPTY_DATE_ROW }] });
  const removeDateRow = (i) => setForm({ ...form, session_dates: form.session_dates.filter((_, idx) => idx !== i) });
  const updateDateRow = (i, field, value) => {
    const next = [...form.session_dates];
    next[i] = { ...next[i], [field]: value };
    if (field === "sessions_count") {
      // Mark this row's sessions as manually overridden so subsequent
      // start / end / minutes-per-session changes don't blow it away.
      next[i]._manual_sessions = true;
    } else if (field === "start_time" || field === "end_time") {
      if (!next[i]._manual_sessions) {
        const auto = computeSessionCount(next[i].start_time, next[i].end_time, form.minutes_per_session);
        next[i].sessions_count = String(auto);
      }
    }
    setForm({ ...form, session_dates: next });
  };

  // When the global Duration per Session changes, recompute every row that
  // hasn't been manually overridden.
  const updateMinutesPerSession = (value) => {
    const mps = Number(value) || 0;
    const next = (form.session_dates || []).map((sd) => {
      if (sd._manual_sessions) return sd;
      return { ...sd, sessions_count: String(computeSessionCount(sd.start_time, sd.end_time, mps)) };
    });
    setForm({ ...form, minutes_per_session: value, session_dates: next });
  };

  const totalSessions = useMemo(() => {
    if (!form) return 0;
    return form.session_dates.reduce((acc, sd) => acc + (Number(sd.sessions_count) || 0), 0);
  }, [form]);

  // Count of UNIQUE dates picked across all session rows (skips blank dates)
  const totalDays = useMemo(() => {
    if (!form) return 0;
    return new Set(form.session_dates.map((sd) => sd.date).filter(Boolean)).size;
  }, [form]);

  const invoiceAuto = useMemo(() => {
    if (!form) return 0;
    return totalSessions * Number(form.school_rate_per_session || 0);
  }, [form, totalSessions]);

  const invoiceFinal = useMemo(() => {
    if (!form) return 0;
    const ov = form.invoice_total_override;
    return ov === "" || ov === null || ov === undefined ? invoiceAuto : Number(ov);
  }, [form, invoiceAuto]);

  // ---- Employee helpers ----
  const updateEmp = (idx, field, value) => {
    const next = [...form.employee_calculations];
    next[idx] = { ...next[idx], [field]: value };
    // If user typed into working_days, flag it as manual so the auto-sync
    // useEffect below stops overwriting it.
    if (field === "working_days") next[idx]._manual_wd = true;
    if (field === "total_sessions") next[idx]._manual_sess = true;
    // When the employee switches INTO per_day mode, auto-populate working_days
    // from the school-invoice Total No. of Days unless the user has already
    // manually edited the value.
    if (field === "mode" && value === "per_day" && !next[idx]._manual_wd) {
      next[idx].working_days = totalDays;
    }
    setForm({ ...form, employee_calculations: next });
  };

  // Auto-sync `working_days` ← Total No. of Days for every per_day employee
  // that hasn't been manually edited. Fires whenever totalDays changes.
  useEffect(() => {
    if (!form || !form.employee_calculations?.length) return;
    let changed = false;
    const next = form.employee_calculations.map((ec) => {
      if (ec.mode !== "per_day") return ec;
      if (ec._manual_wd) return ec;
      if (Number(ec.working_days) === Number(totalDays)) return ec;
      changed = true;
      return { ...ec, working_days: totalDays };
    });
    if (changed) setForm((f) => ({ ...f, employee_calculations: next }));
  }, [totalDays]); // eslint-disable-line

  const empAutoSalary = (ec) => {
    if (ec.mode === "per_day") {
      return Number(ec.rate_amount || 0) * (Number(ec.working_days || 0) + Number(ec.non_working_days || 0));
    }
    return Number(ec.rate_per_session || 0) * Number(ec.total_sessions || 0);
  };

  const empFinalSalary = (ec) => {
    const ov = ec.total_override;
    if (ov === "" || ov === null || ov === undefined) return empAutoSalary(ec);
    return Number(ov);
  };

  // ---- Save ----
  const save = async () => {
    if (!form.month_key) { toast.error("Month is required"); return; }
    setSaving(true);
    try {
      const payload = {
        month_key: form.month_key,
        minutes_per_session: Number(form.minutes_per_session || 0),
        school_rate_per_session: Number(form.school_rate_per_session || 0),
        session_dates: form.session_dates,
        total_sessions: totalSessions,
        total_days: totalDays,
        invoice_total_override: form.invoice_total_override === "" ? null : Number(form.invoice_total_override),
        employee_calculations: form.employee_calculations.map((ec) => ({
          ...ec,
          rate_amount: Number(ec.rate_amount || 0),
          rate_per_session: Number(ec.rate_per_session || 0),
          working_days: Number(ec.working_days || 0),
          non_working_days: Number(ec.non_working_days || 0),
          total_sessions: Number(ec.total_sessions || 0),
          total_override: ec.total_override === "" ? null : Number(ec.total_override),
        })),
        notes: form.notes,
      };
      if (selectedId) {
        await api.put(`/service-orders/${order.id}/calculations/${selectedId}`, payload);
        toast.success("Calculation updated");
      } else {
        const { data } = await api.post(`/service-orders/${order.id}/calculations`, payload);
        setSelectedId(data.id);
        toast.success("Calculation saved");
      }
      loadCalcs();
    } catch (e) { toast.error(formatError(e)); }
    finally { setSaving(false); }
  };

  const removeCalc = async (c) => {
    if (!window.confirm(`Delete calculation for ${c.month_key}?`)) return;
    try {
      await api.delete(`/service-orders/${order.id}/calculations/${c.id}`);
      toast.success("Deleted");
      if (selectedId === c.id) { setForm(null); setSelectedId(null); }
      loadCalcs();
    } catch (e) { toast.error(formatError(e)); }
  };

  const submitForInvoicing = async () => {
    if (!selectedId) return;
    if (!window.confirm("Submit this calculation to Accounts for invoicing? This will lock further edits to the school invoice total.")) return;
    try {
      await api.patch(`/service-orders/${order.id}/calculations/${selectedId}/submit-for-invoicing`);
      toast.success("Submitted to Accounts ✓");
      loadCalcs();
    } catch (e) { toast.error(formatError(e)); }
  };

  const downloadPdf = async (path, filename) => {
    try {
      const res = await api.get(path, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename; document.body.appendChild(a); a.click();
      a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatError(e)); }
  };

  if (loading) return <div className="p-8 text-center text-slate-400">Loading calculations…</div>;

  return (
    <div className="space-y-4">
      {/* Header strip — month list */}
      <div className="flex items-center gap-2 flex-wrap">
        <Calculator size={16} className="text-violet-600" />
        <span className="text-[13px] font-semibold text-slate-700">Saved Months:</span>
        {calcs.length === 0 ? (
          <span className="text-[12px] text-slate-400 italic">None yet</span>
        ) : calcs.map((c) => {
          const submitted = !!c.submitted_for_invoicing;
          const invoiced = !!c.invoiced;
          return (
          <button key={c.id} onClick={() => openExisting(c)} data-testid={`calc-month-${c.month_key}`}
            className={`px-2.5 py-1 text-[11px] font-semibold rounded-full border transition-all ${selectedId === c.id ? "bg-violet-600 border-violet-600 text-white" : "bg-white border-slate-200 hover:border-violet-300 text-slate-700"}`}>
            {c.month_key} · ₹ {Number(c.invoice_total || 0).toLocaleString("en-IN")}
            {invoiced && <span className="ml-1 text-[9px] bg-emerald-500 text-white px-1 rounded">INVOICED</span>}
            {!invoiced && submitted && <span className="ml-1 text-[9px] bg-amber-500 text-white px-1 rounded">SUBMITTED</span>}
          </button>
          );
        })}
        <Button size="sm" variant="outline" onClick={openNew} className="ml-auto" data-testid="calc-new-btn">
          <Plus size={12} className="mr-1" /> New Month
        </Button>
      </div>

      {!form ? (
        <div className="p-8 text-center text-slate-400 text-[13px] border border-dashed rounded">
          Pick a saved month above or click <strong>New Month</strong> to create one.
        </div>
      ) : (
        <Tabs defaultValue="invoice" className="w-full">
          <TabsList>
            <TabsTrigger value="invoice" data-testid="calc-tab-invoice">School Invoice</TabsTrigger>
            <TabsTrigger value="salary" data-testid="calc-tab-salary">Employee Salary · {form.employee_calculations.length}</TabsTrigger>
          </TabsList>

          {/* ===================== INVOICE TAB ===================== */}
          <TabsContent value="invoice" className="space-y-3 mt-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <div>
                <Label className="text-[10px] uppercase font-semibold text-slate-500">Month (YYYY-MM)</Label>
                <Input type="month" value={form.month_key} onChange={(e) => setForm({ ...form, month_key: e.target.value })} data-testid="calc-month" />
              </div>
              <div>
                <Label className="text-[10px] uppercase font-semibold text-slate-500">Duration per Session (min)</Label>
                <Input type="number" min={1} value={form.minutes_per_session} onChange={(e) => updateMinutesPerSession(e.target.value)} className="mono" data-testid="calc-mins" />
              </div>
              <div>
                <Label className="text-[10px] uppercase font-semibold text-slate-500">Rate per Session (₹)</Label>
                <Input type="number" min={0} step="0.01" value={form.school_rate_per_session} onChange={(e) => setForm({ ...form, school_rate_per_session: e.target.value })} className="mono" data-testid="calc-rate" />
              </div>
            </div>

            <div className="border rounded">
              <div className="bg-slate-50 px-3 py-2 flex items-center justify-between">
                <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Session Dates</Label>
                <Button size="sm" variant="outline" onClick={addDateRow} data-testid="calc-add-date">
                  <Plus size={11} className="mr-1" /> Add Date
                </Button>
              </div>
              {form.session_dates.length === 0 ? (
                <div className="p-4 text-center text-slate-400 text-[12px]">No dates added yet.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm responsive-table">
                    <thead className="bg-slate-50 border-b">
                      <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">
                        <th className="px-2 py-1.5">Date</th>
                        <th className="px-2 py-1.5">Start</th>
                        <th className="px-2 py-1.5">End</th>
                        <th className="px-2 py-1.5">Sessions <span className="text-[9px] normal-case font-normal text-slate-400">(auto · override)</span></th>
                        <th className="px-2 py-1.5">Remark</th>
                        <th className="px-2 py-1.5"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {form.session_dates.map((sd, i) => (
                        <tr key={i} className="border-b last:border-0">
                          <td data-label="Date" className="px-2 py-1.5"><Input type="date" value={sd.date} onChange={(e) => updateDateRow(i, "date", e.target.value)} className="h-8 text-[12px]" data-testid={`calc-date-${i}`} /></td>
                          <td data-label="Start" className="px-2 py-1.5">
                            <Input type="time" value={sd.start_time} onChange={(e) => updateDateRow(i, "start_time", e.target.value)} className="h-8 text-[12px] mono" data-testid={`calc-start-${i}`} />
                            <div className="text-[10px] mono text-slate-500 mt-0.5">{to12Hour(sd.start_time) || "—"}</div>
                          </td>
                          <td data-label="End" className="px-2 py-1.5">
                            <Input type="time" value={sd.end_time} onChange={(e) => updateDateRow(i, "end_time", e.target.value)} className="h-8 text-[12px] mono" data-testid={`calc-end-${i}`} />
                            <div className="text-[10px] mono text-slate-500 mt-0.5">{to12Hour(sd.end_time) || "—"}</div>
                          </td>
                          <td data-label="Sessions" className="px-2 py-1.5"><Input type="number" min={0} value={sd.sessions_count} onChange={(e) => updateDateRow(i, "sessions_count", e.target.value)} className="h-8 text-[12px] mono w-20" data-testid={`calc-sess-${i}`} /></td>
                          <td data-label="Remark" className="px-2 py-1.5"><Input value={sd.remark} onChange={(e) => updateDateRow(i, "remark", e.target.value)} placeholder="optional" className="h-8 text-[12px]" /></td>
                          <td data-label="" className="px-2 py-1.5 text-right">
                            <button onClick={() => removeDateRow(i)} className="text-rose-600 hover:bg-rose-50 p-1 rounded" data-testid={`calc-del-date-${i}`}><Trash size={11} /></button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 p-3 bg-violet-50 border border-violet-200 rounded">
              <div>
                <Label className="text-[10px] uppercase font-semibold text-violet-700">Total No. of Sessions</Label>
                <div className="text-2xl font-extrabold text-violet-900 mono" data-testid="calc-total-sessions">{totalSessions}</div>
                <div className="text-[10.5px] text-violet-700 mt-1">
                  Total No. of Days: <span className="mono font-bold" data-testid="calc-total-days">{totalDays}</span>
                </div>
              </div>
              <div>
                <Label className="text-[10px] uppercase font-semibold text-violet-700">Auto Invoice (₹)</Label>
                <div className="text-2xl font-extrabold text-violet-900 mono" data-testid="calc-invoice-auto">₹ {invoiceAuto.toLocaleString("en-IN")}</div>
              </div>
              <div>
                <Label className="text-[10px] uppercase font-semibold text-violet-700 flex items-center gap-1">
                  Override (₹) {isMasterAdmin && <Badge className="bg-amber-100 text-amber-800 text-[9px]">ADMIN</Badge>}
                </Label>
                <Input type="number" min={0} step="0.01" placeholder="Leave blank to use auto"
                  value={form.invoice_total_override}
                  disabled={!isMasterAdmin}
                  onChange={(e) => setForm({ ...form, invoice_total_override: e.target.value })}
                  className="mono" data-testid="calc-invoice-override" />
                <div className="text-[10px] text-violet-700 mt-1 mono">Final: ₹ {invoiceFinal.toLocaleString("en-IN")}</div>
              </div>
            </div>

            <Textarea placeholder="Notes (optional)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} data-testid="calc-notes" />

            <div className="flex flex-wrap gap-2 justify-end pt-2">
              <Button variant="outline" onClick={save} disabled={saving} data-testid="calc-save">
                <Save size={12} className="mr-1" /> {saving ? "Saving…" : "Save"}
              </Button>
              {selectedId && (
                <Button onClick={() => downloadPdf(`/service-orders/${order.id}/calculations/${selectedId}/invoice-pdf`, `Invoice_${order.order_id || "order"}_${form.month_key}.pdf`)} data-testid="calc-invoice-pdf">
                  <Download size={12} className="mr-1" /> Invoice PDF
                </Button>
              )}
              {selectedId && (() => {
                const cur = calcs.find((c) => c.id === selectedId);
                if (!cur) return null;
                if (cur.invoiced) return <Badge className="bg-emerald-500 text-white">Invoiced · {cur.invoice_no || ""}</Badge>;
                if (cur.submitted_for_invoicing) return <Badge className="bg-amber-500 text-white">Submitted to Accounts</Badge>;
                return (
                  <Button onClick={submitForInvoicing} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="calc-submit-invoicing">
                    Submit for Invoicing
                  </Button>
                );
              })()}
              {selectedId && (
                <Button variant="outline" className="text-rose-600 border-rose-200 hover:bg-rose-50" onClick={() => removeCalc(calcs.find((c) => c.id === selectedId))} data-testid="calc-delete">
                  <Trash size={12} className="mr-1" /> Delete Month
                </Button>
              )}
            </div>
          </TabsContent>

          {/* ===================== SALARY TAB ===================== */}
          <TabsContent value="salary" className="space-y-3 mt-3">
            {form.employee_calculations.length === 0 ? (
              <div className="p-6 text-center text-slate-400 text-[12px] border border-dashed rounded">
                No contractual employees assigned to this order yet. Add some in the Deployed Employees tab first.
              </div>
            ) : (
              <div className="space-y-3">
                {!selectedId && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded flex items-start gap-2 text-[12px] text-amber-900">
                    <AlertCircle size={14} className="mt-0.5 shrink-0" />
                    Save the School Invoice first to enable per-employee Salary PDF download.
                  </div>
                )}
                {form.employee_calculations.map((ec, idx) => {
                  const auto = empAutoSalary(ec);
                  const final = empFinalSalary(ec);
                  return (
                    <div key={ec.employee_id || idx} className="p-3 border rounded bg-white" data-testid={`calc-emp-${idx}`}>
                      <div className="flex items-start justify-between flex-wrap gap-2 mb-2">
                        <div>
                          <div className="font-semibold text-slate-800">{ec.employee_name}</div>
                          <div className="text-[11px] text-slate-500">{ec.job_type || "—"}</div>
                        </div>
                        <div className="flex items-center gap-1.5">
                          {[["per_day", "Per Day"], ["per_session", "Per Session"]].map(([v, l]) => (
                            <button key={v} type="button" onClick={() => updateEmp(idx, "mode", v)}
                              className={`px-3 py-1 text-[11px] font-semibold rounded border ${ec.mode === v ? "bg-[var(--pk-accent)] border-[var(--pk-accent)] text-white" : "bg-white border-slate-200 text-slate-600"}`}
                              data-testid={`calc-emp-${idx}-mode-${v}`}>{l}</button>
                          ))}
                        </div>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        {ec.mode === "per_day" ? (
                          <>
                            <div>
                              <Label className="text-[10px] uppercase font-semibold text-slate-500">Rate / Day (₹)</Label>
                              <Input type="number" min={0} value={ec.rate_amount} onChange={(e) => updateEmp(idx, "rate_amount", e.target.value)} className="mono h-8 text-[12px]" data-testid={`calc-emp-${idx}-rate`} />
                            </div>
                            <div>
                              <Label className="text-[10px] uppercase font-semibold text-slate-500">Working Days</Label>
                              <Input type="number" min={0} value={ec.working_days} onChange={(e) => updateEmp(idx, "working_days", e.target.value)} className="mono h-8 text-[12px]" data-testid={`calc-emp-${idx}-wd`} />
                              <button type="button" onClick={() => updateEmp(idx, "working_days", totalDays)} className="text-[10px] text-violet-600 underline mt-0.5" data-testid={`calc-emp-${idx}-wd-pull`}>
                                Pull from School Invoice ({totalDays})
                              </button>
                            </div>
                            <div>
                              <Label className="text-[10px] uppercase font-semibold text-slate-500">Non-Working Days</Label>
                              <Input type="number" min={0} value={ec.non_working_days} onChange={(e) => updateEmp(idx, "non_working_days", e.target.value)} className="mono h-8 text-[12px]" data-testid={`calc-emp-${idx}-nwd`} />
                            </div>
                            <div>
                              <Label className="text-[10px] uppercase font-semibold text-slate-500">Auto (₹)</Label>
                              <div className="h-8 text-[12px] mono flex items-center text-slate-700">₹ {auto.toLocaleString("en-IN")}</div>
                            </div>
                          </>
                        ) : (
                          <>
                            <div>
                              <Label className="text-[10px] uppercase font-semibold text-slate-500">Rate / Session (₹)</Label>
                              <Input type="number" min={0} value={ec.rate_per_session} onChange={(e) => updateEmp(idx, "rate_per_session", e.target.value)} className="mono h-8 text-[12px]" data-testid={`calc-emp-${idx}-rps`} />
                            </div>
                            <div>
                              <Label className="text-[10px] uppercase font-semibold text-slate-500">Sessions / Month</Label>
                              <Input type="number" min={0} value={ec.total_sessions} onChange={(e) => updateEmp(idx, "total_sessions", e.target.value)} className="mono h-8 text-[12px]" data-testid={`calc-emp-${idx}-sess`} />
                              <button type="button" onClick={() => updateEmp(idx, "total_sessions", totalSessions)} className="text-[10px] text-violet-600 underline mt-0.5" data-testid={`calc-emp-${idx}-pull`}>
                                Pull from school ({totalSessions})
                              </button>
                            </div>
                            <div>
                              <Label className="text-[10px] uppercase font-semibold text-slate-500">Auto (₹)</Label>
                              <div className="h-8 text-[12px] mono flex items-center text-slate-700">₹ {auto.toLocaleString("en-IN")}</div>
                            </div>
                            <div />
                          </>
                        )}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-2 items-end">
                        <div className="md:col-span-2">
                          <Label className="text-[10px] uppercase font-semibold text-slate-500">Remark</Label>
                          <Input value={ec.remark} onChange={(e) => updateEmp(idx, "remark", e.target.value)} placeholder="Optional" className="h-8 text-[12px]" data-testid={`calc-emp-${idx}-remark`} />
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase font-semibold text-slate-500 flex items-center gap-1">
                            Override Salary (₹) {isMasterAdmin && <Badge className="bg-amber-100 text-amber-800 text-[9px]">ADMIN</Badge>}
                          </Label>
                          <Input type="number" min={0} value={ec.total_override}
                            disabled={!isMasterAdmin}
                            onChange={(e) => updateEmp(idx, "total_override", e.target.value)}
                            placeholder="auto" className="mono h-8 text-[12px]" data-testid={`calc-emp-${idx}-override`} />
                        </div>
                      </div>

                      <div className="flex items-center justify-between mt-3 pt-2 border-t flex-wrap gap-2">
                        <Badge className="bg-emerald-100 text-emerald-800 text-[11px] mono" data-testid={`calc-emp-${idx}-final`}>
                          Total: ₹ {final.toLocaleString("en-IN")}
                        </Badge>
                        {selectedId && (
                          <Button size="sm" variant="outline" onClick={() => downloadPdf(`/service-orders/${order.id}/calculations/${selectedId}/salary-pdf/${idx}`, `Salary_${ec.employee_name.replace(/\s+/g, "_")}_${form.month_key}.pdf`)} data-testid={`calc-emp-${idx}-pdf`}>
                            <Download size={11} className="mr-1" /> Salary PDF
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="flex justify-end pt-2">
              <Button variant="outline" onClick={save} disabled={saving} data-testid="calc-save-salary">
                <Save size={12} className="mr-1" /> {saving ? "Saving…" : "Save Calculations"}
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
